import { AxTabbedBase } from '@theme/ax-tabbed-base';

/**
 * Media grid tabs — static-content variant of the tabbed pattern.
 *
 * All tab panels are server-rendered (no Section Rendering API fetch needed),
 * so we override `handleTabClick` to bypass the base's lazy-load-on-click flow
 * and just toggle ARIA + hidden via the public surface (`resetActiveTab`).
 *
 * Subclass contract from AxTabbedBase:
 * - `static bemPrefix` is consumed by the base for carousel selectors; this
 *   section has no carousels, but the field is still required to be a string.
 * - `connectedCallback` (inherited) waits for `swiper-container` to be defined
 *   and iterates `this.refs.carousels`. We don't render any carousels, so the
 *   loop is a no-op — harmless.
 *
 * @extends {AxTabbedBase}
 */
class AxMediaGridTabsComponent extends AxTabbedBase {
  static bemPrefix = 'ax-media-grid-tabs';

  /**
   * Switch tabs without hitting the Section Rendering API.
   *
   * @param {number} index
   * @param {Event} [_event]
   */
  async handleTabClick(index, _event) {
    if (typeof index !== 'number') return;

    const { tabs, panels } = this.refs;
    if (!tabs || !panels) return;

    if (tabs[index]?.getAttribute('aria-selected') === 'true') return;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== index;
    }

    this.resetActiveTab(index);

    // Tabs 2+ were rendered with cfh-lazy attributes; re-trigger the observer so
    // the newly visible media gets its src/srcset rewritten and starts loading.
    document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));

    this.#triggerPendingAutoplays(panels[index]);
  }

  /**
   * Resume autoplay on videos that were rendered with `video_autoplay: false`
   * because their panel was hidden at initial render. The snippet marks these
   * wrappers with `data-ax-autoplay-on-show`; we click their deferred-media
   * poster button (the same handler the user would trigger manually), which
   * inserts the <video> from <template> and starts playback.
   *
   * @param {HTMLElement | undefined} panel
   */
  #triggerPendingAutoplays(panel) {
    if (!panel) return;

    const pending = panel.querySelectorAll('[data-ax-autoplay-on-show]');

    for (const wrapper of pending) {
      const playButton = wrapper.querySelector('.deferred-media__poster-button');
      if (playButton instanceof HTMLElement) {
        playButton.click();
      }
      // Mark consumed so further tab clicks don't re-trigger.
      wrapper.removeAttribute('data-ax-autoplay-on-show');
    }
  }
}

customElements.define('ax-media-grid-tabs-component', AxMediaGridTabsComponent);
